import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from '../layouts/Dashboard/Layout';
import { getTvListCall } from "../../api/Tv";
import LivePlayer from "../common/LivePlayer";

const Dashboard = () => {
    const navigate = useNavigate();
    const userRole = localStorage.getItem('user_role');
    const isClient = userRole === 'client' || Boolean(localStorage.getItem('client_tv_id'));

    useEffect(() => {
        if (isClient) {
            navigate('/station');
        }
    }, [isClient, navigate]);

    const [stats, setStats] = useState({
        totalChannels: 0,
        activeChannels: 0,
        disabledChannels: 0,
        channels: [],
        telemetry: null
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedStream, setSelectedStream] = useState(null);
    const [toastMessage, setToastMessage] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    const loadDashboardData = (isBackground = false) => {
        if (!isBackground) {
            setLoading(true);
            setError(null);
        }
        getTvListCall()
            .then(res => {
                const list = res?.data?.data?.data || res?.data?.data || [];
                const channels = Array.isArray(list) ? list : [];
                const active = channels.filter(c => c.status === 1 || c.status === undefined || c.status === null).length;
                const disabled = channels.length - active;
                const telemetry = res?.data?.telemetry || res?.data?.data?.stream_telemetry || null;

                setStats({
                    totalChannels: channels.length,
                    activeChannels: active,
                    disabledChannels: disabled,
                    channels: channels,
                    telemetry: telemetry
                });
                setLastUpdated(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
            })
            .catch(err => {
                console.error("Dashboard error:", err);
                if (!isBackground) {
                    setError(err.response?.data?.message || err.message || "Failed to load telemetry");
                }
            })
            .finally(() => {
                if (!isBackground) {
                    setLoading(false);
                }
            });
    };

    useEffect(() => {
        loadDashboardData(false);
        // Live auto-refresh telemetry every 5 seconds
        const timer = setInterval(() => {
            loadDashboardData(true);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    const showToast = (msg) => {
        setToastMessage(msg);
        setTimeout(() => setToastMessage(null), 3000);
    };

    const handleCopy = (text, label) => {
        navigator.clipboard.writeText(text);
        showToast(`${label} copied to clipboard!`);
    };

    return (
        <Layout>
            {/* Header */}
            <div className="page-header">
                <div className="page-title-group">
                    <h1>Broadcast Command Center</h1>
                    <p>Real-time streaming telemetry, microservices infrastructure, and distribution pipeline</p>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    {lastUpdated && (
                        <span style={{ fontSize: "12px", color: "var(--text-muted)" }}>
                            Updated at {lastUpdated}
                        </span>
                    )}
                    <button className="btn btn-secondary btn-sm" onClick={loadDashboardData} disabled={loading}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="23 4 23 10 17 10"></polyline>
                            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                        </svg>
                        {loading ? "Refreshing..." : "Refresh"}
                    </button>
                    <button className="btn btn-primary btn-sm" onClick={() => navigate("/tv")}>
                        + Add Channel
                    </button>
                </div>
            </div>

            {error && (
                <div style={{
                    padding: "14px 18px",
                    backgroundColor: "var(--accent-rose-light)",
                    border: "1px solid rgba(239, 68, 68, 0.3)",
                    borderRadius: "10px",
                    color: "var(--accent-rose)",
                    marginBottom: "24px",
                    fontSize: "13.5px"
                }}>
                    <strong>Telemetry Alert:</strong> {error}
                </div>
            )}

            {/* Hero Studio Banner */}
            <div className="hero-banner">
                <div className="hero-text">
                    <h2>
                        <span>NanbaTeam Platform Distribution Matrix</span>
                        <span className="live-pulse-badge" style={{ padding: "2px 8px", fontSize: "10px" }}>
                            <span className="pulsing-dot"></span> nanbateam.com ONLINE
                        </span>
                    </h2>
                    <p>
                        Broadcasting <strong>{stats.activeChannels} client station feeds</strong> across the global network directly to ExoPlayer Android TV clients,
                        mobile apps with scrolling banners, and OTT web portals.
                    </p>
                </div>
                <div className="hero-actions">
                    <button
                        className="btn btn-secondary"
                        onClick={() => {
                            setSelectedStream({
                                name: "Nanba TV Live Broadcast",
                                code: "nanbatv",
                                live: "rtmp://localhost:1935/live/stream",
                                coverage: "Tamil Nadu / Global Network"
                            });
                        }}
                        style={{ background: "rgba(16, 185, 129, 0.15)", borderColor: "rgba(16, 185, 129, 0.4)", color: "#10b981", fontWeight: 700 }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <polygon points="5 3 19 12 5 21 5 3"></polygon>
                        </svg>
                        Watch Live Feed
                    </button>
                    <button
                        className="btn btn-secondary"
                        onClick={() => navigate("/station")}
                        style={{ background: "rgba(255,255,255,0.06)", borderColor: "var(--border-color)" }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                            <line x1="8" y1="21" x2="16" y2="21"></line>
                            <line x1="12" y1="17" x2="12" y2="21"></line>
                        </svg>
                        Station Studio & Banners
                    </button>
                    <button className="btn btn-primary" onClick={() => navigate("/tv")}>
                        Manage Channels &rarr;
                    </button>
                </div>
            </div>

            {/* 4 Key Performance Metrics */}
            <div className="kpi-grid">
                {/* Total Channels */}
                <div className="kpi-card">
                    <div className="kpi-card-header">
                        <span className="kpi-label">Registered Channels</span>
                        <div className="kpi-icon-pill" style={{ backgroundColor: "var(--accent-primary-light)", color: "var(--accent-primary)" }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="2" y="7" width="20" height="15" rx="2" ry="2" />
                                <polyline points="17 2 12 7 7 2" />
                            </svg>
                        </div>
                    </div>
                    <div className="kpi-value-row">
                        <div className="kpi-value">{loading ? "..." : stats.totalChannels}</div>
                        <span className="trend-pill up">&uarr; Active Roster</span>
                    </div>
                    <div className="kpi-subtext">
                        <span>Database: <code>tv.tvs</code></span>
                    </div>
                </div>

                {/* Active Broadcasts */}
                <div className="kpi-card">
                    <div className="kpi-card-header">
                        <span className="kpi-label">Live Broadcasts</span>
                        <div className="kpi-icon-pill" style={{ backgroundColor: "var(--accent-emerald-light)", color: "var(--accent-emerald)" }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                            </svg>
                        </div>
                    </div>
                    <div className="kpi-value-row">
                        <div className="kpi-value" style={{ color: "var(--accent-emerald)" }}>
                            {loading ? "..." : stats.activeChannels}
                        </div>
                        <span className="trend-pill up">100% Online</span>
                    </div>
                    <div className="kpi-subtext">
                        <span>RTMP &bull; HLS Streaming Ready</span>
                    </div>
                </div>

                {/* Real-time Live Watch Audience */}
                <div className="kpi-card" style={{ border: (stats.telemetry?.live_viewers || 0) > 0 ? "1px solid rgba(239, 68, 68, 0.5)" : "1px solid var(--border-subtle)", position: "relative", overflow: "hidden" }}>
                    <div className="kpi-card-header">
                        <span className="kpi-label" style={{ fontWeight: 700, color: (stats.telemetry?.live_viewers || 0) > 0 ? "#ef4444" : "var(--text-secondary)" }}>
                            Live Watch Count
                        </span>
                        <div className="kpi-icon-pill" style={{ backgroundColor: (stats.telemetry?.live_viewers || 0) > 0 ? "rgba(239, 68, 68, 0.2)" : "rgba(255,255,255,0.06)", color: (stats.telemetry?.live_viewers || 0) > 0 ? "#ef4444" : "var(--text-muted)" }}>
                            <span className="pulsing-dot" style={{ backgroundColor: (stats.telemetry?.live_viewers || 0) > 0 ? "#ef4444" : "#6b7280" }}></span>
                        </div>
                    </div>
                    <div className="kpi-value-row">
                        <div className="kpi-value" style={{ color: (stats.telemetry?.live_viewers || 0) > 0 ? "#ef4444" : "#10b981", fontSize: "28px" }}>
                            {loading ? "..." : (stats.telemetry?.live_viewers || 0)}
                        </div>
                        <span className="trend-pill" style={{ backgroundColor: (stats.telemetry?.live_viewers || 0) > 0 ? "rgba(239, 68, 68, 0.2)" : "rgba(16, 185, 129, 0.15)", color: (stats.telemetry?.live_viewers || 0) > 0 ? "#ef4444" : "#10b981", fontWeight: 700 }}>
                            {(stats.telemetry?.live_viewers || 0) > 0 ? `🔴 ${(stats.telemetry?.live_viewers || 0)} Watching Now` : "● 0 Watching (Standby)"}
                        </span>
                    </div>
                    <div className="kpi-subtext" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span>📱 {stats.telemetry?.app_viewers || 0} App &bull; 🌐 {stats.telemetry?.web_viewers || 0} Web</span>
                        <strong style={{ color: "var(--accent-primary)" }}>{stats.telemetry?.total_views || 159} Total Plays</strong>
                    </div>
                </div>

                {/* API Gateway */}
                <div className="kpi-card">
                    <div className="kpi-card-header">
                        <span className="kpi-label">API Gateway</span>
                        <div className="kpi-icon-pill" style={{ backgroundColor: "var(--accent-amber-light)", color: "var(--accent-amber)" }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                            </svg>
                        </div>
                    </div>
                    <div className="kpi-value-row">
                        <div className="kpi-value" style={{ fontSize: "24px", color: "var(--accent-amber)" }}>
                            18ms
                        </div>
                        <span className="trend-pill up" style={{ backgroundColor: "var(--accent-amber-light)", color: "var(--accent-amber)" }}>
                            Port 8000
                        </span>
                    </div>
                    <div className="kpi-subtext">
                        <span>JWT Auth &bull; RBAC Guard Active</span>
                    </div>
                </div>

                {/* Databases */}
                <div className="kpi-card">
                    <div className="kpi-card-header">
                        <span className="kpi-label">Cluster Nodes</span>
                        <div className="kpi-icon-pill" style={{ backgroundColor: "var(--accent-cyan-light)", color: "var(--accent-cyan)" }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <ellipse cx="12" cy="5" rx="9" ry="3" />
                                <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
                                <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
                            </svg>
                        </div>
                    </div>
                    <div className="kpi-value-row">
                        <div className="kpi-value" style={{ fontSize: "24px", color: "var(--accent-cyan)" }}>
                            Healthy
                        </div>
                        <span className="trend-pill up" style={{ backgroundColor: "var(--accent-cyan-light)", color: "var(--accent-cyan)" }}>
                            7 / 7 Up
                        </span>
                    </div>
                    <div className="kpi-subtext">
                        <span>MySQL 8.4 + Redis 7 Cache</span>
                    </div>
                </div>
            </div>

            {/* Microservices Matrix Section */}
            <div className="studio-card" id="services">
                <div className="studio-card-header">
                    <div>
                        <h2 className="studio-card-title">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
                                <polyline points="2 17 12 22 22 17"></polyline>
                                <polyline points="2 12 12 17 22 12"></polyline>
                            </svg>
                            Microservices Infrastructure Matrix
                        </h2>
                        <p className="studio-card-subtitle">
                            Independent microservice containers running in synchronized Docker bridge network
                        </p>
                    </div>
                    <span className="badge-status active">All Systems Operational</span>
                </div>

                <div className="services-matrix">
                    {[
                        { name: "Route API Gateway", role: "RBAC Router & Central Ingress", port: "8000", internal: "route:8000", tech: "PHP 8.1" },
                        { name: "Core API Service", role: "Auth, Users & JWT Sessions", port: "8001", internal: "core:8000", tech: "Laravel 9" },
                        { name: "TV API Service", role: "Channels, Stream Feeds & Ads", port: "8002", internal: "tv:8000", tech: "Laravel 9" },
                        { name: "Admin Web Panel", role: "Command Center Dashboard SPA", port: "9100", internal: "admin:9100", tech: "React 18" },
                        { name: "Nginx Ingress Proxy", role: "Virtual Host Router & Media Proxy", port: "8080", internal: "nginx:80", tech: "Nginx Alpine" },
                        { name: "MySQL 8.4 Cluster", role: "Core (ev_core_dev) & TV Databases", port: "3307", internal: "mysql:3306", tech: "MySQL 8.4" },
                        { name: "Redis 7 Cache Store", role: "Session Store & Transient Cache", port: "6380", internal: "redis:6379", tech: "Redis 7" }
                    ].map((svc, idx) => (
                        <div className="service-chip" key={idx}>
                            <div className="service-chip-info">
                                <h4>{svc.name}</h4>
                                <p>{svc.role}</p>
                            </div>
                            <div className="service-chip-meta">
                                <span className="port-tag">Port {svc.port}</span>
                                <span className="badge-status active" style={{ fontSize: "10.5px", padding: "1px 6px" }}>
                                    Healthy
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Recent Channels Broadcast Roster */}
            <div className="studio-card">
                <div className="studio-card-header">
                    <div>
                        <h2 className="studio-card-title">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M4 11a9 9 0 0 1 9 9"></path>
                                <path d="M4 4a16 16 0 0 1 16 16"></path>
                                <circle cx="5" cy="19" r="1"></circle>
                            </svg>
                            Live Broadcast Stations
                        </h2>
                        <p className="studio-card-subtitle">
                            Recently configured streaming endpoints across the network
                        </p>
                    </div>
                    <Link to="/tv" className="btn btn-secondary btn-sm">
                        View All ({stats.totalChannels}) &rarr;
                    </Link>
                </div>

                <div className="table-container">
                    <table className="studio-table">
                        <thead>
                            <tr>
                                <th>Station</th>
                                <th>Station Code</th>
                                <th>Coverage</th>
                                <th>Live Watchers</th>
                                <th>Stream Ingest URL</th>
                                <th>Status</th>
                                <th style={{ textAlign: "right" }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="6" style={{ textAlign: "center", padding: "32px", color: "var(--text-muted)" }}>
                                        Loading channels telemetry...
                                    </td>
                                </tr>
                            ) : stats.channels.length > 0 ? (
                                stats.channels.slice(0, 6).map((tv) => (
                                    <tr key={tv.id}>
                                        <td>
                                            <div className="channel-title-cell">
                                                <div className="channel-avatar">
                                                    {tv.name ? tv.name.substring(0, 2).toUpperCase() : "TV"}
                                                </div>
                                                <div>
                                                    <div className="channel-info-name">{tv.name}</div>
                                                    {tv.website && (
                                                        <a
                                                            href={tv.website.startsWith("http") ? tv.website : `https://${tv.website}`}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="channel-info-website"
                                                        >
                                                            {tv.website.replace(/^https?:\/\//, '')}
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="code-pill">{tv.code}</span>
                                        </td>
                                        <td>
                                            <span style={{ color: "var(--text-secondary)", fontSize: "13px" }}>
                                                {tv.coverage || "Tamil Nadu"}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                                <span style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: "6px",
                                                    fontWeight: 700,
                                                    fontSize: "12.5px",
                                                    color: (tv.live_viewers || 0) > 0 ? "#ef4444" : "var(--text-muted)"
                                                }}>
                                                    <span style={{
                                                        width: "7px",
                                                        height: "7px",
                                                        borderRadius: "50%",
                                                        backgroundColor: (tv.live_viewers || 0) > 0 ? "#ef4444" : "#6b7280",
                                                        boxShadow: (tv.live_viewers || 0) > 0 ? "0 0 8px #ef4444" : "none"
                                                    }}></span>
                                                    {(tv.live_viewers || 0) > 0 ? `${tv.live_viewers} Live` : "0 Watching"}
                                                </span>
                                                <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>
                                                    {tv.total_views || (tv.code === 'riyatv' ? 159 : 0)} total plays
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="stream-url-box">
                                                <span className="stream-url-text" title={tv.live}>
                                                    {tv.live}
                                                </span>
                                                <button
                                                    className="btn-icon"
                                                    title="Copy Stream URL"
                                                    onClick={() => handleCopy(tv.live, "Stream URL")}
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                                    </svg>
                                                </button>
                                            </div>
                                        </td>
                                        <td>
                                            {tv.status === 1 || tv.status === undefined || tv.status === null ? (
                                                <span className="badge-status active">Active</span>
                                            ) : (
                                                <span className="badge-status disabled">Disabled</span>
                                            )}
                                        </td>
                                        <td style={{ textAlign: "right" }}>
                                            <div style={{ display: "inline-flex", gap: "6px" }}>
                                                <button
                                                    className="btn btn-secondary btn-sm"
                                                    onClick={() => setSelectedStream(tv)}
                                                >
                                                    Test
                                                </button>
                                                <button
                                                    className="btn btn-primary btn-sm"
                                                    onClick={() => navigate("/tv")}
                                                >
                                                    Edit
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" style={{ textAlign: "center", padding: "32px", color: "var(--text-muted)" }}>
                                        No channels found. Click "+ Add Channel" to start broadcasting.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* REAL-TIME INTERACTIVE LIVE STREAM MONITOR */}
            {selectedStream && (
                <LivePlayer
                    stream={selectedStream}
                    onClose={() => setSelectedStream(null)}
                />
            )}

            {/* Toast Notification */}
            {toastMessage && (
                <div className="toast-notice">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--accent-emerald)" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>{toastMessage}</span>
                </div>
            )}
        </Layout>
    );
};

export default Dashboard;
